// SCR-STAFF-201 تب قیمت (US-CAT-020 تا 022، 025): قیمت جاری فهرست پایه، تاریخچه نسخه‌ها، افزودن قیمت با روش Fixed، PerUnit (پله‌های
// حجمی نهایی — BR-CAT-023) یا Range (حداقل و حداکثر — BR-CAT-022) با تاریخ مؤثر امروز یا آینده (BR-CAT-026) و قیمت ویژه ارائه‌دهنده یا گروه
// ارائه‌دهنده (BR-CAT-024). ورود مبلغ در واحد نمایش مستأجر و ذخیره ریالی (MoneyFieldRHF، ADR-013).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type PriceEntryView, type ServiceView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ConfirmDialog,
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  useDisplaySettings,
  zBusinessDate,
  zMoneyMinor,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useFieldArray, useForm, useWatch, type DefaultValues } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { catKeys, SavedNotice, todayIn, useProviderGroups, useProviders } from './common';

const STATE_TONE: Record<PriceEntryView['state'], StatusTone> = {
  Current: 'active',
  Scheduled: 'booked',
  Past: 'inactive',
  Superseded: 'cancelled',
};

/** وضعیت نسخه قیمت. */
export function PriceStateChip({ state }: { state: PriceEntryView['state'] }) {
  return <StatusChip tone={STATE_TONE[state]} labelKey={`cat:price_state.${state}`} />;
}

function priceSchema(today: string) {
  return z
    .object({
      target: z.string(),
      method: z.enum(['Fixed', 'PerUnit', 'Range']),
      amount: zMoneyMinor(),
      maxAmount: z.number().int().min(0).optional(),
      tiers: z.array(z.object({ fromQuantity: z.string(), unitAmount: z.number().int().min(0).optional() })),
      effectiveFrom: zBusinessDate(),
    })
    .superRefine((value, ctx) => {
      if (value.effectiveFrom && value.effectiveFrom < today) {
        ctx.addIssue({
          code: 'custom',
          path: ['effectiveFrom'],
          message: 'errors:error.price_effective_date_past',
        });
      }
      if (
        value.method === 'Range' &&
        (value.maxAmount === undefined || value.maxAmount < Number(value.amount ?? 0))
      ) {
        ctx.addIssue({ code: 'custom', path: ['maxAmount'], message: 'errors:validation.range' });
      }
      let previous = 1;
      value.tiers.forEach((tier, index) => {
        const from = Number(tier.fromQuantity);
        if (!Number.isInteger(from) || from <= previous) {
          ctx.addIssue({
            code: 'custom',
            path: ['tiers', index, 'fromQuantity'],
            message: 'errors:validation.tiers',
          });
        }
        if (tier.unitAmount === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['tiers', index, 'unitAmount'],
            message: 'common:validation.required',
          });
        }
        previous = Number.isInteger(from) ? from : previous;
      });
    });
}
type PriceForm = z.input<ReturnType<typeof priceSchema>>;

export function PriceTab({ service }: { service: ServiceView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.price.manage');
  const providers = useProviders();
  const groups = useProviderGroups();
  const { timeZone } = useDisplaySettings();
  const today = todayIn(timeZone);
  const idempotency = useIdempotencyKey();
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  // BR-CAT-035: قیمت بالاتر از تعرفه مصوب (PHX-CAT-0331) با تأیید صریح ثبت می‌شود (AC-CAT-033.2).
  const [confirmAbove, setConfirmAbove] = useState(false);
  const [aboveTariff, setAboveTariff] = useState<{ approved: number; requested: number } | null>(null);

  const history = useQuery({
    queryKey: catKeys.history(session?.tenantId, service.id),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/price-lists/base/entries', {
          params: { query: { serviceId: service.id } },
        }),
      ),
  });

  const defaults: DefaultValues<PriceForm> = {
    target: '',
    method: 'Fixed',
    amount: undefined,
    maxAmount: undefined,
    tiers: [],
    effectiveFrom: today,
  };
  const { control, handleSubmit, formState, setError, reset } = useForm<PriceForm>({
    resolver: zodResolver(priceSchema(history.data?.today ?? today)),
    defaultValues: defaults,
  });
  const method = useWatch({ control, name: 'method' });
  const tiers = useFieldArray({ control, name: 'tiers' });

  const add = useMutation({
    mutationFn: (form: PriceForm) =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/price-lists/base/entries', {
          body: {
            serviceId: service.id,
            providerId: form.target.startsWith('p:') ? form.target.slice(2) : null,
            providerGroupId: form.target.startsWith('g:') ? form.target.slice(2) : null,
            method: form.method,
            amount: { amount: Number(form.amount), currency: 'IRR' },
            maxAmount:
              form.method === 'Range' ? { amount: Number(form.maxAmount), currency: 'IRR' } : undefined,
            tiers:
              form.method === 'PerUnit' && form.tiers.length > 0
                ? [
                    { fromQuantity: 1, unitAmount: { amount: Number(form.amount), currency: 'IRR' } },
                    ...form.tiers.map((tier) => ({
                      fromQuantity: Number(tier.fromQuantity),
                      unitAmount: { amount: Number(tier.unitAmount), currency: 'IRR' },
                    })),
                  ]
                : null,
            effectiveFrom: form.effectiveFrom,
            confirmAboveApprovedTariff: confirmAbove,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setSaved(true);
      setAboveTariff(null);
      setConfirmAbove(false);
      reset(defaults);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'PHX-CAT-0331') {
        const problem = error as unknown as { approved?: number; requested?: number };
        setAboveTariff({ approved: Number(problem.approved ?? 0), requested: Number(problem.requested ?? 0) });
        return;
      }

      const applied = applyServerErrors(error, setError, {
        fields: ['amount', 'maxAmount', 'effectiveFrom'],
        codeFields: {
          'PHX-CAT-0701': 'effectiveFrom',
          'PHX-CAT-0702': 'effectiveFrom',
          'PHX-CAT-0302': 'target',
          'PHX-CAT-0710': 'target',
        },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const items = history.data?.items ?? [];
  const general = (entry: PriceEntryView) => !entry.providerId && !entry.providerGroupId;
  const groupName = (id: string | null | undefined) =>
    groups.data?.items.find((group) => group.id === id)?.name ?? id ?? '';
  const current = items.find((entry) => entry.state === 'Current' && general(entry));
  const scheduled = items
    .filter((entry) => entry.state === 'Scheduled' && general(entry))
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))[0];

  const columns: DataTableColumn<PriceEntryView>[] = [
    {
      id: 'effectiveFrom',
      headerKey: 'cat:prices.columns.effective_from',
      cell: (row) => <JalaliDate value={row.effectiveFrom} />,
    },
    {
      id: 'amount',
      headerKey: 'cat:prices.columns.amount',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>
            <Money amountMinor={row.amount.amount} currency={row.amount.currency} />
            {row.maxAmount ? (
              <>
                {' – '}
                <Money amountMinor={row.maxAmount.amount} currency={row.maxAmount.currency} />
              </>
            ) : null}
          </span>
          {(row.tiers ?? []).slice(1).map((tier) => (
            <Typography key={String(tier.fromQuantity)} variant="caption" color="text.secondary">
              {t('cat:prices.tier_from', { from: String(tier.fromQuantity) })}{' '}
              <Money amountMinor={tier.unitAmount.amount} currency={tier.unitAmount.currency} />
            </Typography>
          ))}
        </Stack>
      ),
    },
    {
      id: 'provider',
      headerKey: 'cat:prices.columns.provider',
      cell: (row) =>
        row.providerId
          ? providers.name(row.providerId)
          : row.providerGroupId
            ? t('cat:prices.group_named', { name: groupName(row.providerGroupId) })
            : t('cat:prices.general'),
    },
    { id: 'method', headerKey: 'cat:prices.columns.method', cell: (row) => t(`cat:method.${row.method}`) },
    {
      id: 'state',
      headerKey: 'cat:prices.columns.state',
      cell: (row) => <PriceStateChip state={row.state} />,
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline">{t('cat:prices.current')}</Typography>
            <Typography variant="h5" component="p" data-testid="price-current">
              {current ? (
                <Money amountMinor={current.amount.amount} currency={current.amount.currency} />
              ) : (
                t('cat:prices.not_defined')
              )}
            </Typography>
            {current && (
              <Typography variant="body2" color="text.secondary">
                {t('cat:prices.since')} <JalaliDate value={current.effectiveFrom} />
              </Typography>
            )}
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="overline">{t('cat:prices.scheduled')}</Typography>
            <Typography variant="h5" component="p" data-testid="price-scheduled">
              {scheduled ? (
                <Money amountMinor={scheduled.amount.amount} currency={scheduled.amount.currency} />
              ) : (
                t('common:states.none')
              )}
            </Typography>
            {scheduled && (
              <Typography variant="body2" color="text.secondary">
                {t('cat:prices.from')} <JalaliDate value={scheduled.effectiveFrom} />
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          component="form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (write.canWrite) {
              void handleSubmit((form) => add.mutate(form))();
            }
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle1">{t('cat:prices.add_title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('cat:prices.add_help_methods')}
            </Typography>
            <WriteNotice write={write} />
            <SavedNotice show={saved && !formState.isDirty} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <SelectRHF
                control={control}
                name="target"
                labelKey="cat:prices.provider"
                helperKey="cat:prices.provider_help"
                fullWidth
                disabled={!write.canWrite}
                options={[
                  { value: '', labelKey: 'cat:prices.general' },
                  ...(groups.data?.items ?? []).map((group) => ({
                    value: `g:${group.id}`,
                    label: t('cat:prices.group_named', { name: group.name }),
                  })),
                  ...providers.options.map((option) => ({ value: `p:${option.id}`, label: option.name })),
                ]}
              />
              <SelectRHF
                control={control}
                name="method"
                labelKey="cat:prices.method"
                fullWidth
                disabled={!write.canWrite}
                options={(['Fixed', 'PerUnit', 'Range'] as const).map((value) => ({
                  value,
                  labelKey: `cat:method.${value}`,
                }))}
              />
              <MoneyFieldRHF
                control={control}
                name="amount"
                labelKey={
                  method === 'Range'
                    ? 'cat:prices.min_amount'
                    : method === 'PerUnit'
                      ? 'cat:prices.unit_amount'
                      : 'cat:prices.amount'
                }
                required
                fullWidth
                disabled={!write.canWrite}
              />
              {method === 'Range' && (
                <MoneyFieldRHF
                  control={control}
                  name="maxAmount"
                  labelKey="cat:prices.max_amount"
                  required
                  fullWidth
                  disabled={!write.canWrite}
                />
              )}
              <JalaliDatePickerRHF
                control={control}
                name="effectiveFrom"
                labelKey="cat:prices.effective_from"
                helperKey="cat:prices.effective_from_help"
                required
                disabled={!write.canWrite}
              />
            </Stack>
            {method === 'PerUnit' && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">{t('cat:prices.tiers_help')}</Typography>
                  <Button
                    startIcon={<AddIcon />}
                    disabled={!write.canWrite || tiers.fields.length >= 9}
                    onClick={() => tiers.append({ fromQuantity: '', unitAmount: undefined })}
                    data-testid="price-tier-add"
                  >
                    {t('cat:prices.add_tier')}
                  </Button>
                </Stack>
                {tiers.fields.map((field, index) => (
                  <Stack
                    key={field.id}
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems="flex-start"
                    data-testid={`price-tier-${index}`}
                  >
                    <TextFieldRHF
                      control={control}
                      name={`tiers.${index}.fromQuantity`}
                      labelKey="cat:prices.tier_from_quantity"
                      type="number"
                      required
                      disabled={!write.canWrite}
                      slotProps={{ htmlInput: { min: 2, 'data-testid': `price-tier-from-${index}` } }}
                    />
                    <MoneyFieldRHF
                      control={control}
                      name={`tiers.${index}.unitAmount`}
                      labelKey="cat:prices.unit_amount"
                      required
                      disabled={!write.canWrite}
                    />
                    <IconButton
                      aria-label={t('cat:prices.remove_tier')}
                      disabled={!write.canWrite}
                      onClick={() => tiers.remove(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
          {submitError ? <ErrorState error={submitError} compact /> : null}
          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 2 }}
            disabled={!write.canWrite || add.isPending}
            data-testid="price-submit"
          >
            {t('cat:prices.add')}
          </Button>
        </Box>
      </Paper>

      <DataTable
        captionKey="cat:prices.history"
        columns={columns}
        rows={items}
        getRowId={(row) => row.id}
        isLoading={history.isPending}
        error={history.error}
        onRetry={() => void history.refetch()}
        emptyTitleKey="cat:prices.history_empty"
      />
      <ConfirmDialog
        open={aboveTariff !== null}
        titleKey="cat:prices.above_tariff_title"
        messageKey="cat:prices.above_tariff_message"
        messageValues={{
          approved: faDigits(aboveTariff?.approved ?? 0),
          requested: faDigits(aboveTariff?.requested ?? 0),
        }}
        confirmKey="cat:prices.above_tariff_confirm"
        busy={add.isPending}
        onConfirm={() => {
          setConfirmAbove(true);
          void handleSubmit((form) => add.mutate(form))();
        }}
        onCancel={() => setAboveTariff(null)}
      />
    </Stack>
  );
}
